import { types, flow, Instance } from 'mobx-state-tree';
import axios from 'server';
import { SyncUserView, SyncGroupsView, SyncDepartmentsView } from './types';
import { AxiosResponse } from 'axios';
import {
  syncUserModel,
  IsyncUserModel,
  syncGroupModel,
  IsyncGroupModel,
  IsyncDepartmentModel,
  syncDepartmentModel,
  ISelectedUserModel,
} from './models/googletable.model';
import { toFormatDateTime } from 'utils';

const { model, array, maybeNull, reference, string } = types;
export const GoogleStore = model('GoogleStore', {
  syncusers: array(syncUserModel),
  syncgroups: array(syncGroupModel),
  syncToken: maybeNull(string),
  syncdepartments: array(syncDepartmentModel),
  clickedUser: maybeNull(reference(syncUserModel)),
  clickedGroup: maybeNull(reference(syncGroupModel)),
  clickedDepartment: maybeNull(reference(syncDepartmentModel)),
  usersKeywordFilter: string,
  userCount: maybeNull(types.number),
  currentPageSize: maybeNull(types.number),
  hasNextPage: maybeNull(types.boolean),
  maxResults: maybeNull(types.number),
})
  .volatile((self) => ({
    matchCurrentBase(): boolean {
      return true;
    },
  }))
  .views((self) => ({
    get allsyncUsersView(): SyncUserView[] {
      const allsyncUsersView: SyncUserView[] = [];
      for (let i = 0; i < self.syncusers.length; i++) {
        allsyncUsersView.push({ ...self.syncusers[i], id: i });
      }
      return allsyncUsersView;
    },
    get allsyncGroupsView(): SyncGroupsView[] {
      const allsyncGroupsView: SyncGroupsView[] = [];
      for (let i = 0; i < self.syncgroups.length; i++) {
        allsyncGroupsView.push({ ...self.syncgroups[i], id: i });
      }
      return allsyncGroupsView;
    },
    get allsyncDepartmentsView(): SyncDepartmentsView[] {
      const allsyncDepartmentsView: SyncDepartmentsView[] = [];
      for (let i = 0; i < self.syncdepartments.length; i++) {
        allsyncDepartmentsView.push({ ...self.syncdepartments[i], id: i });
      }
      return allsyncDepartmentsView;
    },
    get getUsersKeywordFilter(): string {
      return self.usersKeywordFilter;
    },
    get totalUserCount(): number {
      return self.userCount || 0;
    },
    get syncuserView(): SyncUserView[] {
      const usersView: SyncUserView[] = self.syncusers.map((user, index) => ({
        ...user,
        id: index,
      }));
      const filteredByBase = usersView.filter(self.matchCurrentBase);
      if (!self.usersKeywordFilter) {
        return filteredByBase;
      }
      const regexp = new RegExp(self.usersKeywordFilter, 'i');
      const filteredByKeyword = filteredByBase.filter(({ sn, name, creationTimestamp, lastLoginTime, primaryEmail }) =>
        regexp.test(
          `${sn}${name}${toFormatDateTime(creationTimestamp)}${toFormatDateTime(lastLoginTime)}${primaryEmail}`
        )
      );
      return filteredByKeyword;
    },
  }))
  .volatile(() => ({}))
  .actions((self) => ({
    normalizeUsers({ data }: AxiosResponse, isSearch: boolean, isNextToken: boolean): IsyncUserModel[] {
      // Handle the new API response structure
      const responseData = data.data || data;

      self.syncToken = responseData.nextPageToken || null;
      self.userCount = responseData.userCount || 0;

      // Store pagination info from API
      if (responseData.pagination) {
        self.currentPageSize = responseData.pagination.current_page_size || 0;
        self.hasNextPage = responseData.pagination.has_next_page || false;
        self.maxResults = responseData.pagination.max_results || 10;
      }

      if (!responseData.users || responseData.users.length === 0) {
        if (!isNextToken) {
          self.syncusers.clear();
        }
        return self.syncusers;
      }

      const newUsers = responseData.users;

      // For search, replace all data
      if (isSearch) {
        self.syncusers.replace(newUsers);
        return self.syncusers;
      }

      // For next page, replace with new data (not append)
      if (isNextToken) {
        self.syncusers.replace(newUsers);
        return self.syncusers;
      }

      // For first page or refresh, replace all data
      self.syncusers.replace(newUsers);
      return self.syncusers;
    },
    normalizeGroups({ data }: AxiosResponse): IsyncGroupModel[] {
      const groups = data?.data?.groups || data?.groups || [];
      const transformedGroups = groups.map((group: any) => ({
        mail: group.email || group.mail,
        memberCount: group.memberCount,
        nonEditableAliases: group.nonEditableAliases,
        isAdmin: group.isAdmin,
        adminCreated: group.adminCreated,
        cn: group.name || group.cn || '',
        gidNumber: group.gidNumber || group.id,
        description: group.description,
      }));
      self.syncgroups.replace(transformedGroups);
      return self.syncgroups;
    },
    normalizeDepartment({ data }: AxiosResponse): IsyncDepartmentModel[] {
      const departments = data?.data?.departments || data?.departments || [];
      const transformedDepartments = departments.map((dept: any) => ({
        base: dept.name,
        parentOuId: dept.parentOuId,
        ou: dept.name || dept.ou || '',
        ouId: dept.orgUnitId || dept.id,
        description: dept.description,
      }));
      self.syncdepartments.replace(transformedDepartments);
      return self.syncdepartments;
    },
    setUsersKeywordFilter(filter: string): void {
      self.usersKeywordFilter = filter;
    },
    clearSelected(): void {
      self.clickedUser = null;
      self.clickedDepartment = null;
      self.clickedGroup = null;
    },
  }))

  .actions((self) => ({
    syncUser: flow(function* (nextPageToken?: string, search?: string, department?: string) {
      try {
        const isSearch = !!search;
        const isNextToken = !!nextPageToken;

        if (nextPageToken) {
          localStorage.setItem('GoogleToken', JSON.stringify(nextPageToken));
        }

        const params = new URLSearchParams();
        department && params.append('department', department);
        search && params.append('search', search);
        nextPageToken && params.append('nextPageToken', nextPageToken);

        const url = `apps/google/users${params.toString() ? `?${params.toString()}` : ''}`;

        const response = yield axios.get<IsyncUserModel[]>(url);
        return self.normalizeUsers(response, isSearch, isNextToken);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to sync users from Google';
        console.error('Sync user error:', errorMessage);
        throw new Error(errorMessage);
      }
    }),

    reSyncUser: flow(function* () {
      const payload = {
        sync_users: true,
        sync_groups: false,
        sync_departments: false,
        google_to_ldap: true,
        ldap_to_google: true,
      };

      const response = yield axios.post<IsyncUserModel[]>('apps/google/sync/master', payload);
      return response.data;
    }),
    reSyncSingleUser: flow(function* (data: any) {
      const result = yield axios
        .post<IsyncUserModel[]>(`apps/google/sync/user`, data)
        .then((response) => {
          self.clearSelected();
          return response.data;
        })
        .catch((error) => {
          throw error;
        });
      return result;
    }),
    SelectedUser: flow(function* (data: any) {
      const result = yield axios
        .post<ISelectedUserModel[]>(`apps/google/sync/users/bulk`, data)
        .then((response) => {
          self.clearSelected();
          return response.data;
        })
        .catch((error) => {
          const errorMessage = error.response?.data?.message || 'An unknown error occurred';
          throw new Error(errorMessage);
        });
      return result;
    }),
    syncGroup: flow(function* () {
      const result = yield axios
        .get<IsyncGroupModel[]>(`apps/google/groups`)
        .then(self.normalizeGroups)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    reSyncGroup: flow(function* () {
      const result = yield axios
        .get<IsyncGroupModel[]>(`admin/app/google/master-sync/group`)
        .then((res) => {
          return res.data;
        })
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    reSyncSingleGroup: flow(function* (data: any) {
      const result = yield axios
        .post<IsyncGroupModel[]>(`apps/google/sync/group`, data)
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    syncDepartment: flow(function* () {
      return yield axios
        .get<IsyncDepartmentModel[]>(`apps/google/departments`)
        .then(self.normalizeDepartment)
        .catch((error) => {
          throw new Error(error.response?.data?.message || error.message);
        });
    }),
    reSyncDepartment: flow(function* () {
      const result = yield axios
        .get<IsyncDepartmentModel[]>(`admin/app/google/master-sync/department`)
        .then((res) => {
          return res.data;
        })
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    reSyncSingleDepartment: flow(function* (data: any) {
      const result = yield axios
        .post<IsyncDepartmentModel[]>(`apps/google/sync/department`, data)
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
  }));

export type IGoogleStore = Instance<typeof GoogleStore>;
